import {
  MeetingCalendarEvent,
  MeetingJoinPreference,
  MeetingsVisibility,
  TeamCallSettings,
  UserCallSettings,
  CallTranscripts,
  CompletedCallSummary,
  CallSummaryType,
  CallSummaryGeneralContent,
  CallSummarySalesContent,
} from '@distribute/shared/types';

type CheckIsPrivateEvent = {
  event: MeetingCalendarEvent;
  teamSettings: TeamCallSettings;
};

export const checkIsPrivateEvent = ({
  event,
  teamSettings,
}: CheckIsPrivateEvent): boolean => {
  if (event.isPrivate !== null) {
    return event.isPrivate;
  }

  if (teamSettings.meetingsVisibility === MeetingsVisibility.SHARE_ALL) {
    return false;
  }

  if (
    teamSettings.meetingsVisibility === MeetingsVisibility.SHARE_EXTERNAL &&
    event.isExternal
  ) {
    return false;
  }

  if (
    teamSettings.meetingsVisibility === MeetingsVisibility.SHARE_INTERNAL &&
    !event.isExternal
  ) {
    return false;
  }

  // MeetingsVisibility.SHARE_NONE
  return true;
};

type CheckBotShouldRecord = {
  event: MeetingCalendarEvent;
  userSettings: UserCallSettings;
};

export const checkBotShouldRecord = ({
  event,
  userSettings,
}: CheckBotShouldRecord): boolean => {
  if (event.shouldRecord !== null) {
    return event.shouldRecord;
  }

  if (
    userSettings.joinPreference === MeetingJoinPreference.JOIN_ORGANIZED &&
    event.isOrganizedByUser
  ) {
    return true;
  }

  if (userSettings.joinPreference === MeetingJoinPreference.JOIN_ALL) {
    return true;
  }

  if (
    userSettings.joinPreference === MeetingJoinPreference.JOIN_EXTERNAL &&
    event.isExternal
  ) {
    return true;
  }

  // MeetingJoinPreference.NO_AUTO_JOIN
  return false;
};

export const checkIsValidZoomUrl = (url: string) => {
  try {
    const _url = new URL(url);
    const validPaths = [
      'j',
      'w',
      'my',
      'rec',
      'meeting',
      'webinar',
      'start',
      'join',
    ];
    const pathSegments = _url.pathname.split('/').filter(Boolean);

    return (
      _url.protocol === 'https:' &&
      _url.hostname.endsWith('zoom.us') &&
      pathSegments.length > 0 &&
      validPaths.includes(pathSegments[0])
    );
  } catch {
    return false;
  }
};

export const checkIsValidGoogleMeetUrl = (url: string) => {
  try {
    const _url = new URL(url);
    const meetCodePattern = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/;
    const pathSegments = _url.pathname.split('/').filter(Boolean);

    return (
      _url.protocol === 'https:' &&
      _url.hostname === 'meet.google.com' &&
      pathSegments.length === 1 &&
      meetCodePattern.test(pathSegments[0])
    );
  } catch {
    return false;
  }
};

export const getMeetingPlatform = (meetingUrl: string) => {
  const isValidZoomUrl = checkIsValidZoomUrl(meetingUrl);
  const isValidGoogleMeetUrl = checkIsValidGoogleMeetUrl(meetingUrl);

  if (isValidZoomUrl) return 'zoom';
  if (isValidGoogleMeetUrl) return 'googleMeet';

  throw new Error('Invalid meeting URL');
};

export const getCallRecordingsFeatureFlag = (
  featureFlag: boolean,
  email: string
) => {
  const allowedEmailDomains = ['@flexum.io', '@distribute.so'];

  if (allowedEmailDomains.some((eDomain) => email.endsWith(eDomain))) {
    return true;
  }

  // zoom review
  if (email === 'distribute.user@gmail.com') {
    return true;
  }

  return featureFlag;
};

export const formatTranscriptTime = (secs: number) => {
  const truncted = Math.trunc(secs);

  const hours = Math.floor(truncted / 3600);
  const minutes = Math.floor(truncted / 60) % 60;
  const seconds = truncted % 60;

  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? '0' + v : v))
    .filter((v, i) => v !== '00' || i > 0)
    .join(':');
};

export const getTranscriptText = (transcripts: CallTranscripts) => {
  return transcripts.reduce((acc, next) => {
    const text = next.words.map((w) => w.text).join(' ');
    const timestamp = next.words?.[0]?.start_timestamp ?? 0;

    return `${acc}${next.speaker}:\n${formatTranscriptTime(
      timestamp
    )}\n${text}\n\n`;
  }, '');
};

const formatSection = (
  title: string,
  content: string | null | undefined
): string => {
  if (!content) return '';
  return `${title}\n${content}\n\n`;
};

const formatBulletList = (items: string[]): string => {
  return items.map((item) => `• ${item}`).join('\n');
};

const formatAssignedItem = (item: {
  text: string;
  assignedTo?: string | null;
  timeline?: string;
}): string => {
  let result = `• ${item.text}`;
  if (item.assignedTo) result += ` - Assigned to: ${item.assignedTo}`;
  if (item.timeline) result += ` (Due: ${item.timeline})`;
  return result;
};

const getCallSummaryGeneralText = (
  content: CallSummaryGeneralContent
): string => {
  const { meetingPurpose, takeaways, topics, nextSteps, actionItems } = content;

  const sections = [
    formatSection('Meeting Purpose', meetingPurpose),
    formatSection(
      'Key Takeaways',
      formatBulletList(takeaways.map((t) => `${t.text}.`))
    ),
    formatSection(
      'Topics',
      topics
        .map(
          ({ subject, items }) =>
            `${subject}\n${items
              .map((item) => `• ${item.text}: ${item.details}.`)
              .join('\n')}`
        )
        .join('\n\n')
    ),
    formatSection('Next Steps', nextSteps.map(formatAssignedItem).join('\n')),
    formatSection(
      'Action Items',
      (actionItems ?? []).map(formatAssignedItem).join('\n')
    ),
  ];

  return sections.join('').trim();
};

const getCallSummarySalesText = (content: CallSummarySalesContent): string => {
  const {
    prospect,
    callContext,
    seats,
    budget,
    currentState,
    painPoints,
    specificRequirements,
    techStack,
    objections,
    otherSolutions,
    timeline,
    decisionMaker,
    procurementProcess,
    nextSteps,
    questionsWeAsked,
    questionsTheyAsked,
    actionItems,
  } = content;

  const prospectDetails = [
    `• Company: ${prospect.company}`,
    `• Contacts: ${prospect.contacts}`,
    `• Description: ${prospect.description}`,
  ].join('\n');

  const sections = [
    formatSection('Deal Information\n\nProspect Details', prospectDetails),
    formatSection('Call Context', `${callContext}.`),
    formatSection('Seats', `${seats}.`),
    formatSection('Budget', `${budget}.`),
    formatSection('Current State', `${currentState}.`),
    formatSection(
      'Pain Points',
      formatBulletList(painPoints.map((p) => `${p}.`))
    ),
    formatSection(
      'Specific Requirements',
      formatBulletList(specificRequirements.map((r) => `${r}.`))
    ),
    formatSection('Tech Stack', formatBulletList(techStack)),
    formatSection(
      'Objections',
      objections
        .map(
          (obj, idx) =>
            `${idx + 1}. Concern: ${obj.objection}\n   • Status: ${
              obj.status
            }\n   • Resolution: ${obj.resolution}.`
        )
        .join('\n')
    ),
    formatSection(
      'Competitive Landscape',
      `Other solutions being evaluated:\n${formatBulletList(otherSolutions)}`
    ),
    formatSection('Timeline', `${timeline}.`),
    formatSection(
      'Decision Maker',
      `1. Primary: ${decisionMaker.primary}\n2. Other stakeholders: ${decisionMaker.otherStakeholders}`
    ),
    formatSection('Procurement Process', `${procurementProcess}.`),
    formatSection(
      'Next Steps',
      formatBulletList(nextSteps.map((s) => `${s}.`))
    ),
    formatSection(
      'Q&A\n\n Questions We Asked',
      questionsWeAsked
        .map((q, idx) => `${idx + 1}. ${q.question}\n   • ${q.response}.`)
        .join('\n')
    ),
    formatSection(
      'Questions They Asked',
      questionsTheyAsked
        .map((q, idx) => `${idx + 1}. ${q.question}\n   • ${q.response}.`)
        .join('\n')
    ),
    formatSection(
      'Action Items',
      actionItems.map(formatAssignedItem).join('\n')
    ),
  ];

  return sections.join('').trim();
};

export function getSummaryText(callSummary: CompletedCallSummary) {
  switch (callSummary.type) {
    case CallSummaryType.GENERAL:
      return getCallSummaryGeneralText(callSummary.content);
    case CallSummaryType.SALES:
      return getCallSummarySalesText(callSummary.content);
    case CallSummaryType.SALES_BANT:
    case CallSummaryType.SALES_MEDDPICC:
    case CallSummaryType.SALES_SANDLER:
    case CallSummaryType.QA:
    case CallSummaryType.CUSTOMER_SUCCESS:
    case CallSummaryType.DEMO:
      return '';
    default:
      return '';
  }
}
